import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { FaBoxesPacking, FaFilter, FaGlobe, FaLocationDot, FaTruckFast, FaWarehouse } from "react-icons/fa6";
import MatchingWorkflowGraphic from "../images/graphics/matching-workflow.svg";
import PageHero from "../components/pageHero";
import { buildApiUrl, buildAssetUrl, formatList } from "../utils/api";

const normalize = (value = "") => value.toLowerCase().trim();

function FinderMarketplace() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [service, setService] = useState("");
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(buildApiUrl("/api/warehouses"));
        const warehouseList = Array.isArray(response.data) ? response.data : [];
        setWarehouses(warehouseList);
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        setWarehouses([]);
        setError("We could not load the warehouse directory right now. Please verify the backend URL and CORS settings on the hosted server.");
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, []);

  const locations = [...new Set(warehouses.flatMap((item) => formatList(item.locations)))].sort();
  const services = [...new Set(warehouses.flatMap((item) => formatList(item.services)))].sort();
  const products = [...new Set(warehouses.flatMap((item) => formatList(item.products)))].sort();

  const filtered = warehouses.filter((item) => {
    const searchable = `${item.name} ${item.locations} ${item.services} ${item.products} ${item.description}`.toLowerCase();
    const matchesQuery = !query || searchable.includes(normalize(query));
    const matchesLocation = !location || formatList(item.locations).includes(location);
    const matchesService = !service || formatList(item.services).includes(service);
    const matchesProduct = !product || formatList(item.products).includes(product);
    const matchesStatus = !status || item.status === status;

    return matchesQuery && matchesLocation && matchesService && matchesProduct && matchesStatus;
  });

  return (
    <>
      <Helmet>
        <title>Browse Warehouses | 3PL Search</title>
        <meta name="description" content="Search global 3PL warehouses by region, service capability, product fit, and fulfillment status." />
      </Helmet>

      <div className="finder-page">
        <PageHero
          eyebrow="Warehouse directory"
          title="Shortlist providers by location, service profile, and operational fit."
          description="Explore the live directory first, then request a guided match only when you want CS support."
          primaryAction={{ to: "/3pl_booking", label: "Request a match" }}
          secondaryAction={{ to: "/contact", label: "Talk to CS" }}
          image={MatchingWorkflowGraphic}
          imageAlt="Warehouse matching workflow infographic"
          imageLight
          compact
        />

        <section className="content-section">
          <div className="container">
            <div className="finder-layout">
              <aside className="filter-panel">
                <div className="filter-panel-head"><FaFilter /> Filters</div>
                <label>Search</label>
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Warehouse, region, service..." />
                <label>Location</label>
                <select value={location} onChange={(event) => setLocation(event.target.value)}>
                  <option value="">All locations</option>
                  {locations.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <label>Service</label>
                <select value={service} onChange={(event) => setService(event.target.value)}>
                  <option value="">All services</option>
                  {services.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <label>Product fit</label>
                <select value={product} onChange={(event) => setProduct(event.target.value)}>
                  <option value="">All products</option>
                  {products.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <label>Status</label>
                <select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">All statuses</option>
                  <option value="Currently Fulfillable">Currently Fulfillable</option>
                  <option value="Not Fulfillable">Not Fulfillable</option>
                </select>
              </aside>

              <div>
                <div className="directory-summary">
                  <div>
                    <span>
                      {loading
                        ? "Loading warehouse directory..."
                        : error
                          ? "Directory unavailable"
                          : `${filtered.length} warehouses found`}
                    </span>
                    <p>
                      {error
                        ? error
                        : "Use the directory to compare options before you raise a requirement."}
                    </p>
                  </div>
                  <Link className="site-secondary-cta" to="/3pl_booking">Submit requirement</Link>
                </div>

                <div className="row g-4">
                  {filtered.map((item) => (
                    <div className="col-md-6 col-xl-4" key={item._id}>
                      <div className="market-card h-100">
                        <div className="market-card-top">
                          <span className={`status-badge ${item.status === "Currently Fulfillable" ? "active" : "inactive"}`}>
                            {item.status || "Status pending"}
                          </span>
                          {item.image?.[0] ? (
                            <img src={buildAssetUrl(item.image[0])} alt={item.name} />
                          ) : (
                            <div className="market-card-fallback"><FaWarehouse /></div>
                          )}
                        </div>
                        <h3>{item.name}</h3>
                        <div className="market-card-meta">
                          <p><FaLocationDot /> {formatList(item.locations).slice(0, 3).join(", ")}</p>
                          <p><FaTruckFast /> {formatList(item.services).slice(0, 2).join(", ")}</p>
                          <p><FaBoxesPacking /> {formatList(item.products).slice(0, 2).join(", ")}</p>
                          <p><FaGlobe /> {item.website}</p>
                        </div>
                        <p className="market-card-copy">{item.description}</p>
                        <div className="market-card-actions">
                          <Link className="site-primary-cta" to={`/warehouseDetails/${item._id}`}>View details</Link>
                          <Link className="text-link" to="/3pl_booking">Need a guided shortlist?</Link>
                        </div>
                      </div>
                    </div>
                  ))}

                  {!loading && error ? (
                    <div className="col-12">
                      <div className="surface-card directory-state-card">
                        <h3>Warehouse data could not be loaded</h3>
                        <p>
                          This usually means the frontend cannot reach the API on the hosted environment.
                          Confirm that `https://searchapi.shipcube.com/api/warehouses` is returning JSON and
                          that the frontend build is using the production API base URL.
                        </p>
                      </div>
                    </div>
                  ) : null}

                  {!loading && !error && filtered.length === 0 ? (
                    <div className="col-12">
                      <div className="surface-card directory-state-card">
                        <h3>No exact matches yet</h3>
                        <p>Try removing a filter or submit a tailored requirement so your CS team can suggest suitable options.</p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default FinderMarketplace;
